import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Container,
  Dialog,
  Heading,
  Icon,
  IconButton,
  LevelBadge,
  Muted,
  NavRow,
  ProgressBar,
  RowGroup,
  Screen,
  StatTile,
  Text,
} from '@fluent/ui';
import { coinsToGel } from '../missions/coinValue.js';
import { useAuth } from '../auth/authStore.js';
import { useProgressSnapshot } from '../achievements/useSnapshot.js';
import { XP_PER_LEVEL, levelProgress, nextLevelXp, xpInLevel, xpToLevel } from '../progress/level.js';
import { supabase } from '../lib/supabase.js';
import { SubscriptionRow } from '../subscription/SubscriptionRow.js';
import { useDerivedSubscriptionStatus, useHasProAccess, useSubscription } from '../subscription/hooks.js';
import { EVERYTHING_FREE } from '../subscription/freeMode.js';
import { useMissions } from '../missions/hooks.js';
import { isGiftSubscription, type DerivedStatus } from '../subscription/access.js';
import { LegalLinks } from '../legal/LegalLinks.js';
import { AppBottomNav } from '../nav/AppBottomNav.js';
import { useIsAdminEmail } from '../admin/constants.js';
import { openInstallModal } from '../pwa/installModalStore.js';
import { isStandalone } from '../pwa/installPromptStore.js';
import {
  BookGlyph,
  CardGlyph,
  CheckCircleGlyph,
  CoinGlyph,
  FlameGlyph,
  GearGlyph,
  PencilGlyph,
  PhoneGlyph,
  ShieldGlyph,
  StarGlyph,
} from '../profile/icons.js';
import { formatMemberSince, toDateLang } from '../lib/dates.js';
import { isNativeApp } from '../native/platform.js';
import './Profile.css';

/**
 * Profile — the account dashboard (trust-first design, DECISIONS
 * 2026-07-17). Read-only surface made of doors: identity hero, one
 * glanceable subscription row (→ /subscription), a stats grid
 * (StatTiles — the future coins tile is a grid insertion), an actions
 * list, and an honest logout. All form controls live in /settings.
 *
 * <ProfileView> is presentational so /profile-review can mount it with
 * fabricated data; the connected wrapper feeds it from the live hooks.
 */
export function Profile() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const snapshot = useProgressSnapshot();
  const hasPro = useHasProAccess();
  const subStatus = useDerivedSubscriptionStatus();
  const { data: subRow } = useSubscription();
  // A subscription Paddle can still charge keeps its door while everything is
  // free, so its owner can see it and cancel. A gift charges nothing.
  const chargeable =
    !isGiftSubscription(subRow) &&
    (subStatus === 'trialing' || subStatus === 'active' || subStatus === 'cancelling' || subStatus === 'past_due');
  const { data: missionsData } = useMissions();
  // Above the `!user` guard: hooks cannot sit behind an early return.
  const showAdmin = useIsAdminEmail(user?.email ?? null);

  if (!user) return null;

  const displayName = profile?.display_name ?? user.email?.split('@')[0] ?? '—';

  return (
    <ProfileView
      data={{
        displayName,
        email: user.email ?? '—',
        avatarUrl: profile?.avatar_url ?? undefined,
        memberSinceIso: profile?.created_at ?? user.created_at ?? null,
        // Everything is free (freeMode.ts): no Pro chip, nothing to pay or fix.
        hasPro: hasPro && !EVERYTHING_FREE,
        subStatus: EVERYTHING_FREE ? 'none' : subStatus,
        showSubscriptionRow: !EVERYTHING_FREE || chargeable,
        totalXp: snapshot.totalXp,
        streakCurrent: snapshot.streak?.current_streak ?? 0,
        streakLongest: snapshot.streak?.longest_streak ?? 0,
        words: snapshot.vocabCount,
        lessons: snapshot.lessonsCompleted,
        coins: missionsData?.balance ?? null,
        monthAwarded: missionsData?.month_awarded ?? null,
        monthCap: missionsData?.month_cap ?? null,
        showInstall: !isStandalone(),
        showAdmin,
      }}
      onEdit={() => navigate('/settings')}
      onOpenSubscription={() => navigate('/subscription')}
      onOpenAwards={() => navigate('/achievements')}
      onSettings={() => navigate('/settings')}
      onInstall={() => openInstallModal()}
      onAdmin={() => navigate('/admin')}
      onLogout={async () => {
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
      }}
    />
  );
}

export interface ProfileViewData {
  displayName: string;
  email: string;
  avatarUrl?: string;
  memberSinceIso: string | null;
  hasPro: boolean;
  subStatus: DerivedStatus;
  totalXp: number;
  streakCurrent: number;
  streakLongest: number;
  words: number;
  lessons: number;
  /** Coin balance; null while unknown (missions RPC not yet resolved). */
  coins: number | null;
  monthAwarded: number | null;
  monthCap: number | null;
  showInstall: boolean;
  showAdmin: boolean;
  /** The subscription door. Default shown; hidden while everything is free
   *  unless a live subscription still needs it. */
  showSubscriptionRow?: boolean;
}

export interface ProfileViewProps {
  data: ProfileViewData;
  onEdit: () => void;
  onOpenSubscription: () => void;
  onOpenAwards: () => void;
  onSettings: () => void;
  onInstall: () => void;
  onAdmin: () => void;
  onLogout: () => Promise<void> | void;
  /** Review-route override for the hook-connected subscription row. */
  subscriptionSlot?: ReactNode;
}

export function ProfileView({
  data,
  onEdit,
  onOpenSubscription,
  onOpenAwards,
  onSettings,
  onInstall,
  onAdmin,
  onLogout,
  subscriptionSlot,
}: ProfileViewProps) {
  const { t, i18n } = useTranslation();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const level = xpToLevel(data.totalXp);
  const xpThisLevel = xpInLevel(data.totalXp);
  const xpRemaining = nextLevelXp(data.totalXp) - data.totalXp;
  const nf = (n: number) => n.toLocaleString(i18n.language === 'ka' ? 'ka-GE' : 'en-US');

  const memberSince = data.memberSinceIso
    ? formatMemberSince(data.memberSinceIso, toDateLang(i18n.language))
    : null;

  return (
    <Screen>
      <Container size="sm">
        <div className="fluent-profile">
          {/* ---------- identity hero ---------- */}
          <section className="fluent-profile__hero">
            <div className="fluent-profile__hero-top">
              <div className="fluent-profile__avatar" data-pro={data.hasPro || undefined}>
                <Avatar size={72} name={data.displayName} src={data.avatarUrl} />
              </div>
              <div className="fluent-profile__identity">
                <div className="fluent-profile__name-row">
                  <Heading level={2} as="h1">
                    {data.displayName}
                  </Heading>
                  {data.hasPro && (
                    <span className="fluent-profile__pro-chip">{t('profile.pro_chip')}</span>
                  )}
                </div>
                <Text tone="muted" size="sm" className="fluent-profile__email">
                  {data.email}
                </Text>
                {memberSince && (
                  <Muted className="fluent-profile__since">
                    {t('profile.member_since', { date: memberSince })}
                  </Muted>
                )}
              </div>
              <IconButton
                className="fluent-profile__edit"
                label={t('profile.edit_aria')}
                onPress={onEdit}
              >
                <Icon size={20}>
                  <PencilGlyph />
                </Icon>
              </IconButton>
            </div>
            <div className="fluent-profile__hero-divider" aria-hidden="true" />
            <div className="fluent-profile__level">
              <div className="fluent-profile__level-row">
                <LevelBadge level={level} size="lg" label={t('profile.level_label')} />
                <Text tone="muted" size="sm" weight="bold">
                  {`${nf(xpThisLevel)} / ${nf(XP_PER_LEVEL)} XP`}
                </Text>
              </div>
              <ProgressBar
                value={levelProgress(data.totalXp)}
                tone="xp"
                size="lg"
                label={t('profile.level_progress', { remaining: xpRemaining })}
              />
              <Muted>{t('profile.level_progress', { remaining: nf(xpRemaining) })}</Muted>
            </div>
          </section>

          {/* ---------- past-due alert (money problems outrank stats) ----------
              Not in the store app: "fix your card" is a payment action, and
              the app may not lead anyone to one (DECISIONS 2026-09-21). */}
          {data.subStatus === 'past_due' && !isNativeApp() && (
            <section className="fluent-profile__alert" role="alert">
              <span className="fluent-profile__alert-icon" aria-hidden="true">
                <Icon size={24}>
                  <CardGlyph />
                </Icon>
              </span>
              <div className="fluent-profile__alert-text">
                <Text weight="bold" style={{ display: 'block' }}>
                  {t('profile.past_due.title')}
                </Text>
                <Text tone="muted" size="sm" style={{ display: 'block' }}>
                  {t('profile.past_due.body')}
                </Text>
              </div>
              <Button variant="danger" size="md" fullWidth onClick={onOpenSubscription}>
                {t('profile.past_due.cta')}
              </Button>
            </section>
          )}

          {/* ---------- subscription door ---------- */}
          {subscriptionSlot ??
            (data.showSubscriptionRow !== false && <SubscriptionRow onOpen={onOpenSubscription} />)}

          {/* ---------- coin wallet — its own indicator, gold identity ---------- */}
          <NavRow
            className="fluent-profile__coins"
            icon={
              <span className="fluent-profile__coins-tile" aria-hidden="true">
                <Icon size={24}>
                  <CoinGlyph />
                </Icon>
              </span>
            }
            title={
              data.coins === null
                ? '—'
                : t('missions.balance_coins', { count: nf(data.coins) })
            }
            sub={
              // What the balance is WORTH, not just this month's tally — the
              // row used to leave "0 მონეტა" meaning nothing (audit F091).
              // While everything is free there is no payment to discount.
              data.coins !== null && data.coins > 0 && !EVERYTHING_FREE
                ? t('profile.coins_sub', { gel: coinsToGel(data.coins) })
                : data.monthAwarded !== null && data.monthCap !== null
                  ? t('missions.month_line', { awarded: data.monthAwarded, cap: data.monthCap })
                  : undefined
            }
            onPress={onOpenAwards}
          />

          {/* ---------- stats grid (coins tile slots in here later) ---------- */}
          <div className="fluent-profile__stats">
            <StatTile
              tone="streak"
              icon={
                <Icon size={20}>
                  <FlameGlyph />
                </Icon>
              }
              value={nf(data.streakCurrent)}
              label={t('profile.stats.streak')}
              sub={t('profile.stats.streak_sub', { longest: nf(data.streakLongest) })}
            />
            <StatTile
              tone="xp"
              icon={
                <Icon size={20}>
                  <StarGlyph />
                </Icon>
              }
              value={nf(data.totalXp)}
              label={t('profile.stats.xp')}
            />
            <StatTile
              tone="primary"
              icon={
                <Icon size={20}>
                  <BookGlyph />
                </Icon>
              }
              value={nf(data.words)}
              label={t('profile.stats.words')}
            />
            <StatTile
              tone="info"
              icon={
                <Icon size={20}>
                  <CheckCircleGlyph />
                </Icon>
              }
              value={nf(data.lessons)}
              label={t('profile.stats.lessons')}
            />
          </div>

          {/* ---------- actions ---------- */}
          <RowGroup>
            <NavRow
              variant="row"
              icon={
                <span className="fluent-profile__row-tile" data-tone="neutral" aria-hidden="true">
                  <Icon size={22}>
                    <GearGlyph />
                  </Icon>
                </span>
              }
              title={t('profile.actions.settings')}
              sub={t('profile.actions.settings_sub')}
              onPress={onSettings}
            />
            {data.showInstall && (
              <NavRow
                variant="row"
                icon={
                  <span className="fluent-profile__row-tile" data-tone="primary" aria-hidden="true">
                    <Icon size={22}>
                      <PhoneGlyph />
                    </Icon>
                  </span>
                }
                title={t('profile.actions.install')}
                sub={t('profile.actions.install_sub')}
                onPress={onInstall}
              />
            )}
            {data.showAdmin && (
              <NavRow
                variant="row"
                icon={
                  <span className="fluent-profile__row-tile" data-tone="neutral" aria-hidden="true">
                    <Icon size={22}>
                      <ShieldGlyph />
                    </Icon>
                  </span>
                }
                title={t('profile.actions.admin')}
                onPress={onAdmin}
              />
            )}
          </RowGroup>

          {/* ---------- logout ---------- */}
          <Button
            variant="ghost"
            size="lg"
            fullWidth
            className="fluent-profile__logout"
            onClick={() => setLogoutOpen(true)}
          >
            {t('profile.logout')}
          </Button>

          <LegalLinks />
        </div>
      </Container>

      <Dialog
        open={logoutOpen}
        onOpenChange={(open) => {
          if (!loggingOut) setLogoutOpen(open);
        }}
        title={t('profile.logout_dialog.title')}
        description={t('profile.logout_dialog.body')}
        actions={
          <>
            <Button
              variant="neutral"
              size="md"
              onClick={() => setLogoutOpen(false)}
              disabled={loggingOut}
            >
              {t('profile.logout_dialog.stay')}
            </Button>
            <Button
              variant="danger"
              size="md"
              loading={loggingOut}
              onClick={() => {
                setLoggingOut(true);
                void Promise.resolve(onLogout()).finally(() => setLoggingOut(false));
              }}
            >
              {t('profile.logout_dialog.confirm')}
            </Button>
          </>
        }
      />

      <AppBottomNav />
    </Screen>
  );
}
